import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Stethoscope } from 'lucide-react';
import SectionActions from './SectionActions';

const COPY = {
  it: {
    eyebrow: 'Chi siamo',
    title_a: 'Non è nato in una sala riunioni.',
    title_b: 'È nato dentro uno studio oculistico.',
    body:
      'Tre cose ci hanno guidato fin dall\'inizio: togliere ore di lavoro ripetitivo alla segreteria, restituire tempo clinico al medico, e farlo senza chiedere allo studio di cambiare il proprio modo di lavorare. Prime Vision è un software italiano, pensato per oculisti italiani, lanciato a inizio 2027.',
    bullets: [
      { k: 'Mission', v: 'Restituire tempo clinico all\'oculista, togliendo passaggi amministrativi.' },
      { k: 'Approccio', v: 'Un solo software, pensato per lavorare in modo ordinato con il percorso FSE 2.0.' },
      { k: 'Stato', v: 'Pre-order aperti · lancio inizio 2027.' },
    ],
    actionLabel: 'Vuoi saperne di più?',
  },
  en: {
    eyebrow: 'About us',
    title_a: 'It was not born in a meeting room.',
    title_b: 'It was born inside an actual eye-care practice.',
    body:
      'Three principles have driven us from day one: remove repetitive front-desk work, give clinical time back to the doctor, and do it without asking the practice to change its way of working. Prime Vision is an Italian software, built for Italian ophthalmologists, launching early 2027.',
    bullets: [
      { k: 'Mission', v: 'Give clinical time back to the ophthalmologist by removing admin steps.' },
      { k: 'Approach', v: 'One software, designed to work in an ordered way with the FSE 2.0 process.' },
      { k: 'Status', v: 'Pre-orders open · launch early 2027.' },
    ],
    actionLabel: 'Want to know more?',
  },
};

/**
 * About — "Chi siamo" section.
 *
 * Props:
 *  - compact?: when true, hides the eyebrow + main title (used on the
 *    standalone /chi-siamo page where the PageHero already prints the H1).
 */
export const About = ({ compact = false }) => {
  const { locale } = useLanguage();
  const c = COPY[locale] || COPY.it;

  return (
    <section
      id="about"
      data-testid="pv-about-section"
      className="relative bg-white py-20 md:py-28 border-t border-slate-100"
    >
      <div className="pv-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT: eyebrow + title (suppressed in compact mode) */}
          {!compact && (
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2">
                <Stethoscope className="w-3.5 h-3.5 text-pv-teal" />
                <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {c.eyebrow}
                </span>
              </div>
              <h2 className="mt-5 font-heading font-bold tracking-normal text-[32px] sm:text-[40px] md:text-[44px] leading-[1.08] text-pv-ink">
                {c.title_a} <br />
                <span className="text-pv-teal">{c.title_b}</span>
              </h2>
            </div>
          )}

          {/* RIGHT: body + bullets + action */}
          <div className={compact ? 'lg:col-span-12 max-w-3xl' : 'lg:col-span-7 max-w-2xl'}>
            <p className="text-slate-600 text-[15.5px] md:text-[17px] leading-[1.7]">
              {c.body}
            </p>

            <dl className="mt-9 md:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-5">
              {c.bullets.map((b, i) => (
                <div key={i}>
                  <dt className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-pv-teal">
                    {b.k}
                  </dt>
                  <dd className="mt-2 text-[13.5px] text-slate-700 leading-relaxed">
                    {b.v}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-9">
              <SectionActions
                label={c.actionLabel}
                source="about"
                items={['whatsapp', 'brochure', 'demo']}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
